import { InteractionStatus } from '@azure/msal-browser';
import { AuthenticatedTemplate, UnauthenticatedTemplate, useMsal } from '@azure/msal-react';
import { useEffect, useMemo, useState } from 'react';
import { Navigate, NavLink, Route, Routes } from 'react-router';
import { ApiError, createApi, type Api } from './api/client';
import { apiScopes } from './auth/msalConfig';
import { ApiContext, MeContext } from './context';
import { RingingBanner } from './components/RingingBanner';
import { CallEventsProvider } from './realtime';
import { AdminPage } from './pages/AdminPage';
import { CallDetailPage } from './pages/CallDetailPage';
import { CallsPage } from './pages/CallsPage';
import { LinesPage } from './pages/LinesPage';
import { HandoversPage } from './pages/HandoversPage';
import { CallTypesPage } from './pages/CallTypesPage';
import { ReportsPage } from './pages/ReportsPage';
import { UsersPage } from './pages/UsersPage';
import type { Me, Role } from './types';

export function App() {
  const { instance, inProgress } = useMsal();
  const api = useMemo(() => createApi(instance), [instance]);

  return (
    <ApiContext.Provider value={api}>
      <UnauthenticatedTemplate>
        <main className="centered">
          <h1>HLHC</h1>
          <p>Sign in with your Microsoft 365 work account to continue.</p>
          <button
            disabled={inProgress !== InteractionStatus.None}
            onClick={() => instance.loginRedirect({ scopes: apiScopes })}
          >
            Sign in with Microsoft
          </button>
        </main>
      </UnauthenticatedTemplate>
      <AuthenticatedTemplate>
        <Shell api={api} />
      </AuthenticatedTemplate>
    </ApiContext.Provider>
  );
}

function Shell({ api }: { api: Api }) {
  const { instance } = useMsal();
  const [me, setMe] = useState<Me | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    api.get<Me>('/api/me').then(setMe, (err: unknown) => {
      setError(err instanceof ApiError ? err.message : 'Could not reach the API');
    });
  }, [api]);

  if (error === 'redirecting_to_signin') {
    return (
      <main className="centered">
        <h1>Session expired</h1>
        <p>Your Microsoft session needs refreshing — sending you back to sign-in…</p>
      </main>
    );
  }
  if (error === 'account_not_enabled') {
    return (
      <main className="centered">
        <h1>Access denied</h1>
        <p>
          Your account has not been enabled for HLHC. Ask an administrator to enable your
          account and assign you a role, then sign in again.
        </p>
        <button onClick={() => instance.logoutRedirect()}>Sign out</button>
      </main>
    );
  }
  if (error) return <main className="centered"><h1>Sign-in problem</h1><p>{error}</p></main>;
  if (!me) return <main className="centered"><p>Loading…</p></main>;

  if (me.roles.length === 0) {
    return (
      <main className="centered">
        <h1>Access not yet granted</h1>
        <p>
          You are signed in as <strong>{me.displayName}</strong>, but no role has been assigned to
          your account. Ask an administrator to grant you access.
        </p>
        <button onClick={() => instance.logoutRedirect()}>Sign out</button>
      </main>
    );
  }

  const has = (...roles: Role[]) => roles.some((r) => me.roles.includes(r));

  return (
    <MeContext.Provider value={me}>
      <CallEventsProvider api={api}>
      <header className="topbar">
        <strong>HLHC</strong>
        <button
          className="nav-toggle"
          aria-label="Menu"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((v) => !v)}
        >
          ☰
        </button>
        <nav className={menuOpen ? 'open' : ''} onClick={() => setMenuOpen(false)}>
          {has('clinical_user', 'admin_user', 'on_call_user', 'manager', 'admin') && <NavLink to="/calls">Calls</NavLink>}
          {has('on_call_user', 'manager', 'admin') && <NavLink to="/lines">Lines</NavLink>}
          {has('on_call_user', 'manager', 'admin') && <NavLink to="/handover">Handover</NavLink>}
          {has('clinical_user', 'admin_user', 'manager', 'admin') && <NavLink to="/reports">Reports</NavLink>}
          {has('manager', 'admin') && <NavLink to="/call-types">Call types</NavLink>}
          {has('admin') && <NavLink to="/users">Users &amp; Access</NavLink>}
          {has('admin') && <NavLink to="/admin">Configuration</NavLink>}
        </nav>
        <span className="spacer" />
        <span className="user-name">{me.displayName}</span>
        <button className="link" onClick={() => instance.logoutRedirect()}>Sign out</button>
      </header>
      <RingingBanner />
      <main>
        <Routes>
          <Route path="/" element={<Navigate to="/calls" replace />} />
          <Route path="/calls" element={<CallsPage />} />
          <Route path="/calls/:id" element={<CallDetailPage />} />
          <Route path="/lines" element={<LinesPage />} />
          <Route path="/handover" element={<HandoversPage />} />
          <Route path="/call-types" element={<CallTypesPage />} />
          <Route path="/reports" element={<ReportsPage />} />
          <Route path="/users" element={<UsersPage />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="*" element={<p>Not found</p>} />
        </Routes>
      </main>
      </CallEventsProvider>
    </MeContext.Provider>
  );
}
